// Driver details › Cars (wave 3, W2_Portal_BookingDetails look): the active
// car as a card (photo, approval and car type as chips, then label / value
// rows), and the driver's other cars as cards beside it.
//
// Data, exactly as before: this tab NEVER downloads the whole /cars node.
// The loaded cars slice, when there is one, paints first (and follows the
// details page's Refresh); then ONE indexed read of just this driver's cars
// (carsByDriverRef, once per driver id) replaces it. Nothing is written.
// "← Back" is the page's own (top left).
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import NoCrashOutlinedIcon from '@mui/icons-material/NoCrashOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import LocalTaxiOutlinedIcon from '@mui/icons-material/LocalTaxiOutlined';
import { getLangKey } from 'common/src/other/getLangKey';
import { FirebaseContext } from 'common';
import { get } from 'firebase/database';
import { tokens as c } from '../theme';
import { EmptyState } from '../components/feedback';
import { formatAddisDateTime } from '../common/exportUtils';
import { DetailCard, InfoRow, StatusChip, calmCaps } from '../components/modern';

// One car: its photo, approval and car type, then the details as rows.
function CarBody({ car, t }) {
  return (
    <>
      {car.car_image ? (
        <Box
          component="img"
          src={car.car_image}
          alt={t('car_image')}
          sx={{ display: 'block', width: '100%', maxWidth: 420, aspectRatio: '3 / 2', objectFit: 'cover', borderRadius: '12px', bgcolor: c.SURFACE_ALT, mb: 1.5 }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            aspectRatio: '3 / 2',
            borderRadius: '12px',
            bgcolor: c.SURFACE_ALT,
            color: c.TEXT_3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            mb: 1.5,
          }}
        >
          <DirectionsCarOutlinedIcon aria-hidden="true" sx={{ fontSize: 40 }} />
          <Typography sx={{ fontSize: 13.5 }}>{t('car_image')}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <StatusChip
          tone={car.approved ? 'success' : 'error'}
          icon={car.approved ? <CheckRoundedIcon /> : <BlockRoundedIcon />}
          label={car.approved ? t('approved') : calmCaps(t('not_approved'))}
        />
        {car.carType ? <StatusChip tone="accent" icon={<LocalTaxiOutlinedIcon />} label={t(getLangKey(car.carType))} /> : null}
      </Box>
      <Box component="dl" sx={{ m: 0 }}>
        <InfoRow label={t('createdAt')}>{car.createdAt ? formatAddisDateTime(car.createdAt) : null}</InfoRow>
        <InfoRow label={t('vehicle_model_name')}>{car.vehicleMake}</InfoRow>
        <InfoRow label={t('vehicle_model_no')}>{car.vehicleModel}</InfoRow>
        <InfoRow label={t('vehicle_no')}>{car.vehicleNumber}</InfoRow>
        <InfoRow label={t('other_info')}>{car.other_info}</InfoRow>
      </Box>
    </>
  );
}

function DriverCars() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState([]);
  const carlistdata = useSelector((state) => state.carlistdata);
  const { carsByDriverRef } = useContext(FirebaseContext);

  // This tab NEVER downloads the whole /cars node (5.8 MB for staff roles).
  // The slice copy, when there is one, paints first and keeps following the
  // driver-detail Refresh button (refreshUser withCars -> UPSERT_DRIVER_CARS).
  useEffect(() => {
    if (carlistdata.cars) {
      setData(carlistdata.cars.filter((item) => item.driver === id.toString()));
    } else {
      setData([]);
    }
  }, [carlistdata.cars, id]);

  // ...and ONE indexed read of just this driver's cars (/cars is indexed on
  // `driver`, ~1-2 KB) replaces it: the slice can be hours old, empty, or
  // seeded with another driver's cars only. Once per mount / driver id; on a
  // read error the slice copy above stays.
  useEffect(() => {
    let cancelled = false;
    get(carsByDriverRef(id.toString()))
      .then((snap) => {
        if (cancelled) return;
        const val = snap.val() || {};
        setData(Object.keys(val).map((k) => ({ ...val[k], id: k })));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const activeCar = useMemo(() => (Array.isArray(data) ? data.filter((item) => item.active === true)[0] : null), [data]);
  const otherCars = useMemo(() => (Array.isArray(data) ? data.filter((item) => item.active !== true) : []), [data]);
  const otherTitle = t('other_cars');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
      <DetailCard icon={<DirectionsCarOutlinedIcon />} hue="success" title={t('active_car')} list={false}>
        {activeCar ? (
          <CarBody car={activeCar} t={t} />
        ) : (
          <EmptyState compact icon={<NoCrashOutlinedIcon />} title={t('no_active_car')} />
        )}
      </DetailCard>

      <Box component="section" aria-label={otherTitle} sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        {otherCars.length > 0 ? (
          otherCars.map((car, index) => (
            <DetailCard
              key={car.id || index}
              icon={<DirectionsCarOutlinedIcon />}
              hue="neutral"
              title={`${otherTitle} · ${car.vehicleNumber || index + 1}`}
              list={false}
            >
              <CarBody car={car} t={t} />
            </DetailCard>
          ))
        ) : (
          <Paper sx={{ p: 2 }}>
            <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK }}>
              {otherTitle}
            </Typography>
            <EmptyState compact icon={<NoCrashOutlinedIcon />} title={t('no_others_car')} />
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default DriverCars;
